<template>
  <div
    ref="container"
    class="timeline-section critical-component-clear"
    :style="{
      '--num-events': blocks.length
    }"
    :class="[
      `timeline-section--desktop-size-${settings.desktop_height}`,
      `timeline-section--mobile-size-${settings.mobile_height}`,
    ]"
  >
    <timeline-headline
      :settings="settings"
      class="is-mobile"
      :sticky="settings.mobile_height !== '0'"
    />

    <div
      ref="target"
      class="timeline-section__scroll-container"
    >
      <timeline-headline
        ref="headline"
        :settings="settings"
        class="is-desktop"
        :sticky="settings.desktop_height !== '0'"
      />

      <div class="timeline-section__sticky-container">
        <div
          ref="scrollX"
          class="timeline-section__timeline"
        >
          <span class="timeline-section__progress-bar">
            <span
              ref="progress"
              class="timeline-section__progress"
              :style="{
                width: `${progress * 100}%`
              }"
            />
          </span>

          <ul class="timeline-section__events">
            <timeline-event
              v-for="(block, index) in blocks"
              :key="block.id"
              ref="events"
              :event="block"
              :active="currentBlock >= index"
            />
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Timeline section (timeline-section)
 * -----------------------------------------------------------------------------
 * Display content in a timeline format with scroll pinning.
 *
 */
import { mapState } from 'vuex'
import { scroll, animate } from 'motion'

import TimelineHeadline from '~async/timeline-section/timeline-headline'
import TimelineEvent from '~async/timeline-section/timeline-event'

export default {
  name: 'TimelineSection',

  components: {
    TimelineHeadline,
    TimelineEvent,
  },

  props: {
    settings: {
      type: Object,
      default: () => ({

      }),
    },
    blocks: {
      type: Array,
      default: () => ([]),
    },
  },

  data() {
    return {
      animations: [],
      currentBlock: 0,
      initialLoad: true,
      scrollOptions: {},
      progress: 0,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
      screenWidth: (state) => state.index.screen.width,
    }),
  },

  watch: {
    screenWidth() {
      this.initScrollAnimation()
    },
  },

  mounted() {
    this.scrollOptions = {
      target: this.$refs.target,
    }
    this.initScrollAnimation()
  },

  methods: {

    /**
     * Initialize scroll animations
     */
    async initScrollAnimation() {
      await this.$nextTick()

      // Cleanup animations before restarting
      this.animations.forEach((animation) => {
        animation()
      })
      this.animations = []

      this.updateTimeline()
      this.updateProgressDots()

      // Trigger scroll animation updates on initial page load
      if (this.initialLoad) {
        window.scrollTo(0, window.scrollY + 1)
        this.initialLoad = false
      }
    },

    /**
     * Update timeline position as user scrolls
     */
    updateTimeline() {
      const gutter = this.isMobile ? 16 : 48
      const headingContentSize = this.isMobile ? gutter : (300 + gutter + 116)

      const slideGap = this.isMobile ? 64 : 164
      const slideSize = (this.isMobile ? 516 : 532) + slideGap
      const totalSize = headingContentSize + slideSize * this.blocks.length + gutter * 2

      const numScreens = totalSize / window.innerWidth - 1
      const transform = `translateX(-${numScreens * 100}vw)`

      this.animations.push(scroll(
        animate(this.$refs.scrollX, { transform: ['none', transform] }),
        this.scrollOptions,
      ))

      if (!this.isMobile) {
        this.animations.push(scroll(
          animate(this.$refs.headline.$el, { transform: ['none', transform] }),
          this.scrollOptions,
        ))
      }
    },

    /**
     * Activate dots on the timeline when they intersect with the active progress bar
     */
    updateProgressDots() {
      this.animations.push(scroll(
        (timelineProgress) => {
          this.progress = timelineProgress

          const progress = this.$refs.progress.getBoundingClientRect().right
          let lastIndex = 0
          this.$refs.events.forEach((block, index) => {
            if (block.$refs.dot.getBoundingClientRect().left <= progress) {
              lastIndex = index
            }
          })
          this.currentBlock = lastIndex
        },
        this.scrollOptions,
      ))
    },
  },
}
</script>

<style lang="scss">
@import './timeline-section';
</style>
